import { checkIsInternalCompany } from 'app/auth/authUtils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { RootCategory, RootTreeCategories, TaskScopeGroup } from 'constants/taskCategories';
import { TaskAssignmentType } from 'constants/tasks';
import {
  getTaskCategoriesWithTaskTypesTree,
  getTaskTypesWithoutCategoryTree,
} from 'utils/taskCategories';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { getCurrentSearchValue } from 'store/search/selectors';

import { getSystem } from './system.selectors';
import {
  selectTaskTypesFormattedValues,
  selectTaskTypesValues,
  selectTaskTypesWithoutCategory,
} from './taskTypes.selectors';

export const selectTaskCategories = createSelector(
  getSystem,
  ({ taskCategories }) => taskCategories,
);

export const selectTaskCategoriesValues = createSelector(selectTaskCategories, Object.values);

const selectEnabledTaskCategoriesIds = createSelector(selectTaskCategoriesValues, taskCategories =>
  taskCategories.reduce((acc, { isEnabled, taskCategoryId }) => {
    if (isEnabled) {
      acc.push(taskCategoryId);
    }

    return acc;
  }, []),
);

const selectTaskCategoriesWithTaskTypes = createSelector(
  [selectTaskCategoriesValues, selectTaskTypesFormattedValues],
  (taskCategories, taskTypes) => {
    return _.sortBy(taskCategories, [({ name }) => _.toLower(name)]).reduce((acc, category) => {
      const filteredTaskTypes = taskTypes.filter(
        ({ taskCategoryId }) => taskCategoryId === category.taskCategoryId,
      );
      acc.push({
        ...category,
        taskTypes: filteredTaskTypes,
      });

      return acc;
    }, []);
  },
);

const selectTaskCategoriesWithTaskTypesWithSearch = createSelector(
  [selectTaskCategoriesWithTaskTypes, getCurrentSearchValue],
  (taskCategoriesWithTaskTypes, search) => {
    return taskCategoriesWithTaskTypes.map(category => {
      const filteredTaskTypes = category.taskTypes.filter(({ name }) =>
        name.match(new RegExp(search, 'gim')),
      );

      return { ...category, taskTypes: filteredTaskTypes };
    });
  },
);

export const selectTaskCategoriesWithExistingTaskTypes = createSelector(
  [selectTaskCategoriesValues, selectTaskTypesFormattedValues],
  (taskCategories, taskTypeValues) => {
    return taskCategories.reduce((acc, category) => {
      if (category?.isEnabled) {
        const categoryTaskTypes = taskTypeValues.filter(
          ({ taskCategoryId }) => taskCategoryId === category.taskCategoryId,
        );

        if (!categoryTaskTypes.length) {
          return acc;
        }

        acc.push({
          ...category,
          taskTypes: categoryTaskTypes,
        });
      }

      return acc;
    }, []);
  },
);

const CONTACT_SUPPORTED_TASK_ASSIGNMENT_TYPES = [
  TaskAssignmentType.SLA_GROUP,
  TaskAssignmentType.SUB_COMPANY,
  TaskAssignmentType.SUB_COMPANY_USER,
];

export const selectContactTaskCategories = createSelector(
  [selectTaskCategoriesWithExistingTaskTypes],
  taskCategories =>
    taskCategories.reduce((acc, category) => {
      const contactTaskTypes = category.taskTypes.filter(
        ({ entityAssignmentType, taskAssignmentType }) =>
          _.isNil(entityAssignmentType) &&
          _.includes(CONTACT_SUPPORTED_TASK_ASSIGNMENT_TYPES, taskAssignmentType),
      );

      if (contactTaskTypes.length) {
        acc.push({ ...category, taskTypes: contactTaskTypes });
      }

      return acc;
    }, []),
);

export const selectTaskTypesOptions = createSelector(
  [selectTaskCategoriesWithTaskTypes, selectMasterCompanyId],
  (taskCategories, masterCompanyId) => {
    const isInternalViewer = checkIsInternalCompany({ companyId: masterCompanyId });

    return taskCategories.reduce((acc, { taskTypes, name, isEnabled, companyId }) => {
      if (isEnabled) {
        const isGlobal = checkIsInternalCompany({ companyId }) && !isInternalViewer;
        const taskTypesWithCategoryName = taskTypes.map(taskType => ({
          ...taskType,
          categoryName: name,
          isExpanded: true,
          isGlobal,
        }));
        acc.push(...taskTypesWithCategoryName);
      }

      return acc;
    }, []);
  },
);

export const selectGroupedTaskTypeOptions = createSelector(
  [selectTaskCategoriesWithExistingTaskTypes, selectMasterCompanyId],
  (taskCategories, masterCompanyId) => {
    const isInternalViewer = checkIsInternalCompany({ companyId: masterCompanyId });

    const buildScopeOptions = (categories, groupName) =>
      _.orderBy(
        categories.flatMap(({ name, taskTypes }) =>
          taskTypes.map(taskType => ({ ...taskType, categoryName: name, groupName })),
        ),
        [({ categoryName }) => _.toLower(categoryName), ({ name }) => _.toLower(name)],
      );

    const [globalCategories, localCategories] = _.partition(
      taskCategories,
      ({ companyId }) => checkIsInternalCompany({ companyId }) && !isInternalViewer,
    );

    return [
      ...buildScopeOptions(globalCategories, TaskScopeGroup.GLOBAL),
      ...buildScopeOptions(localCategories, TaskScopeGroup.LOCAL),
    ];
  },
);

export const selectEnabledTaskTypes = createSelector(
  [selectTaskTypesValues, selectEnabledTaskCategoriesIds],
  (taskTypes, enabledTaskCategoriesIds) =>
    taskTypes.reduce((acc, taskType) => {
      if (enabledTaskCategoriesIds.includes(taskType.taskCategoryId)) {
        acc[taskType.taskTypeId] = taskType;
      }

      return acc;
    }, {}),
);

const selectGlobalFormattedTaskCategories = createSelector(
  [selectTaskCategoriesWithTaskTypesWithSearch, selectMasterCompanyId],
  (taskCategories, masterCompanyId) =>
    taskCategories.filter(
      ({ companyId }) =>
        checkIsInternalCompany({ companyId }) &&
        !checkIsInternalCompany({ companyId: masterCompanyId }),
    ),
);

const selectLocalFormattedTaskCategories = createSelector(
  [selectTaskCategoriesWithTaskTypesWithSearch, selectGlobalFormattedTaskCategories],
  (taskCategories, globalTaskCategories) =>
    _.differenceBy(taskCategories, globalTaskCategories, 'taskCategoryId'),
);

export const selectTaskCategoryById = memoizeOne(taskCategoryId =>
  createSelector(selectTaskCategories, taskCategories => taskCategories[taskCategoryId]),
);

export const selectTaskCategoriesTreeItems = createSelector(
  [
    selectMasterCompanyId,
    selectGlobalFormattedTaskCategories,
    selectLocalFormattedTaskCategories,
    selectTaskTypesWithoutCategory,
  ],
  (masterCompanyId, globalTaskCategories, localTaskCategories, withoutCategory) => {
    const isInternalCompany = checkIsInternalCompany({ companyId: masterCompanyId });

    const withoutCategoryTaskTypes = getTaskTypesWithoutCategoryTree({
      taskTypes: withoutCategory,
      isGlobal: false,
      isInternalCompany,
      rootParent: RootCategory.withoutCategory,
    });

    return [
      RootTreeCategories.global,
      ...getTaskCategoriesWithTaskTypesTree({
        taskCategories: globalTaskCategories,
        isGlobal: true,
        isInternalCompany,
        rootParent: RootCategory.global,
      }),
      RootTreeCategories.local,
      ...getTaskCategoriesWithTaskTypesTree({
        taskCategories: localTaskCategories,
        isGlobal: false,
        isInternalCompany,
        rootParent: RootCategory.local,
      }),
      ...(withoutCategoryTaskTypes.length ? [RootTreeCategories.withoutCategory] : []),
      ...withoutCategoryTaskTypes,
    ];
  },
);
